<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import IntegrationEmptyState from '../shared/IntegrationEmptyState.vue';
import IntegrationConnectionCard from '../shared/IntegrationConnectionCard.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const uiFlags = computed(() => getters['googleAds/getUIFlags'].value || {});
const integrations = computed(
  () => getters['googleAds/getGoogleAds'].value || []
);

const isLoading = computed(
  () => uiFlags.value.isFetching || uiFlags.value.isCreating
);
const isEmpty = computed(() => integrations.value.length === 0);
const isConnected = computed(() => integrations.value.length > 0);
const integration = computed(() => integrations.value[0] || null);

const customerId = ref('');
const leadConversionActionId = ref('');
const confirmedConversionActionId = ref('');
const gtagId = ref('');
const leadSendTo = ref('');

onMounted(async () => {
  await store.dispatch('googleAds/get');
  syncForm();
});

const syncForm = () => {
  const settings = integration.value?.settings || {};
  customerId.value = settings.customer_id || '';
  leadConversionActionId.value = settings.lead_conversion_action_id || '';
  confirmedConversionActionId.value =
    settings.confirmed_conversion_action_id || '';
  gtagId.value = settings.gtag_id || '';
  leadSendTo.value = settings.lead_send_to || '';
};

const handleConnect = async () => {
  try {
    await store.dispatch('googleAds/authorize');
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.GOOGLE_ADS.CONNECT_ERROR')
    );
  }
};

const handleDisconnect = async () => {
  // eslint-disable-next-line no-alert
  if (window.confirm(t('INTEGRATION_SETTINGS.GOOGLE_ADS.DISCONNECT.CONFIRM'))) {
    try {
      await store.dispatch('googleAds/delete', integration.value.id);
      useAlert(t('INTEGRATION_SETTINGS.GOOGLE_ADS.DISCONNECT.SUCCESS'));
    } catch (error) {
      useAlert(
        error?.message || t('INTEGRATION_SETTINGS.GOOGLE_ADS.DISCONNECT.ERROR')
      );
    }
  }
};

const handleSaveSettings = async () => {
  try {
    await store.dispatch('googleAds/update', {
      id: integration.value.id,
      customer_id: customerId.value,
      lead_conversion_action_id: leadConversionActionId.value,
      confirmed_conversion_action_id: confirmedConversionActionId.value,
      gtag_id: gtagId.value,
      lead_send_to: leadSendTo.value,
    });
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_ADS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.GOOGLE_ADS.SAVE_ERROR')
    );
  }
};
</script>

<template>
  <SettingsLayout
    :header-title="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.HEADER')"
    :button-label="
      isConnected
        ? $t('INTEGRATION_SETTINGS.GOOGLE_ADS.DISCONNECT_BUTTON')
        : $t('INTEGRATION_SETTINGS.GOOGLE_ADS.CONNECT_BUTTON')
    "
    @click="isConnected ? handleDisconnect() : handleConnect()"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <IntegrationEmptyState
      v-else-if="isEmpty"
      icon="i-lucide-badge-dollar-sign"
      :title="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.EMPTY_STATE')"
      :description="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.DESCRIPTION')"
      :button-label="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.CONNECT_BUTTON')"
      @action="handleConnect"
    />

    <div v-else class="space-y-4">
      <IntegrationConnectionCard
        icon="i-lucide-badge-dollar-sign"
        icon-class="bg-gradient-to-br from-green-500 to-green-700"
        :title="
          integration?.account_name ||
          integration?.settings?.account_name ||
          $t('INTEGRATION_SETTINGS.GOOGLE_ADS.CONNECTED')
        "
        :description="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.CONNECTED_DESCRIPTION')"
      />
      <div class="space-y-3 rounded-lg border border-n-weak p-4">
        <Input
          v-model="customerId"
          :label="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.CUSTOMER_ID')"
          :placeholder="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.CUSTOMER_ID_PLACEHOLDER')"
        />
        <Input
          v-model="leadConversionActionId"
          :label="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.LEAD_CONVERSION_ACTION')"
        />
        <Input
          v-model="confirmedConversionActionId"
          :label="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.CONFIRMED_CONVERSION_ACTION')"
        />
        <Input
          v-model="gtagId"
          :label="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.GTAG_ID')"
          :placeholder="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.GTAG_ID_PLACEHOLDER')"
        />
        <Input
          v-model="leadSendTo"
          :label="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.LEAD_SEND_TO')"
          :placeholder="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.LEAD_SEND_TO_PLACEHOLDER')"
        />
        <Button
          :label="$t('INTEGRATION_SETTINGS.GOOGLE_ADS.SAVE_SETTINGS')"
          @click="handleSaveSettings"
        />
      </div>
    </div>
  </SettingsLayout>
</template>
